<template>
  <!-- Content -->
  <div>
    <div
      :class="
        isDarkMode && !isContributor
          ? 'bg-truegray-700 text-white'
          : 'bg-white text-black'
      "
      class="flex flex-col h-120 w-full relative z-10 pb-60 overflow-x-hidden"
    >
      <!-- Border line -->
      <div
        :class="isDarkMode ? 'bg-white' : 'bg-truegray-300'"
        class="post-intro"
      />

      <h1
        v-if="
          currentPost.category_title &&
          !editingPost &&
          !page &&
          !loading &&
          currentPost.category !== 0
        "
        class="text-category opacity-5 lg:opacity-10 pr-4 m-0 absolute top-16 right-0 lg:top-95/100 lg:right-0 lg:origin-top-right lg:transform lg:rotate-90 pointer-events-none"
      >
        {{ currentPost.category_title }}
      </h1>

      <h5
        class="items-center uppercase ml-4 lg:ml-16 mt-auto cursor-pointer flex"
        :class="{ 'text-cw-alt': isContributor }"
        @click="goBack"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M7 16l-4-4m0 0l4-4m-4 4h18"
          />
        </svg>
        <span>Back</span>
      </h5>

      <div v-if="canEditPost" class="mt-4">
        <button
          type="button"
          class="ml-4 lg:ml-16 mb-0 btn btn-black space-x-1"
          :class="{ 'btn-cw-cd': isContributor }"
          @click="onEditPost"
        >
          <span v-if="!editingPost">
            <span>Edit</span>
            <span v-if="page">page</span>
            <span v-else>post</span>
          </span>
          <span v-else>Cancel</span>
        </button>
      </div>
    </div>

    <div
      class="bg-truegray-100 w-full pb-16 flex flex-col items-center lg:items-start"
    >
      <!-- Post content -->
      <div
        class="lg:min-h-post overflow-hidden rounded-xl lg:ml-16 -mt-48 relative z-20 py-16 px-6 lg:p-16 flex w-11/12 lg:w-9/12 transition-all duration-500"
        :class="[
          loading
            ? 'bg-truegray-500 items-center justify-center delay-100'
            : 'bg-white',
          !isDarkMode || isContributor ? 'border-2 border-truegray-100' : '',
        ]"
      >
        <PostContent v-if="!editingPost" :post_id="post_id" :page="page" />
        <PostUpdate
          v-else-if="userAccess"
          v-show="!loading"
          :post-id="post_id"
          :is-loading="loading"
          @update-loading="updateLoading"
        />
        <div
          v-if="loading"
          class="h-48 w-48 absolute right-1/2 bottom-1/2 transform translate-x-1/2 translate-y-1/2"
        >
          <lottie :options="loadingAnimationOptions" />
        </div>
      </div>
    </div>

    <PostsRelatedPanel v-if="!page" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { shared } from '../../../../js/mixins/shared.js'
import { EventBus } from '../../../../js/plugins/event-bus.js'
import PostContent from '../components/PostContent.vue'
import PostUpdate from '../../manager/pages/posts/PostUpdate.vue'
import PostsRelatedPanel from '../components/PostsRelatedPanel.vue'
import * as loadingAnimation from '../../../../shared/components/loading.json'

export default {
  components: {
    PostContent,
    PostsRelatedPanel,
    PostUpdate,
  },

  mixins: [shared],

  props: {
    post_id: {
      type: Number,
      required: false,
    },

    edit: {
      type: Boolean,
      required: false,
    },

    page: { type: Boolean, default: false },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,
      loading: false,

      window: {
        width: 0,
        height: 0,
      },

      editingPost: false,
    }
  },

  computed: {
    ...mapGetters({
      settings: 'shared/getSettings',
      userAccess: 'shared/getUserAccess',
      isContributor: 'shared/isContributor',
      currentPost: 'shared/getCurrentPost',
      customisation: 'careerwise/getCustomisation',
      isDarkMode: 'careerwise/isDarkMode',
    }),

    canEditPost() {
      return this.userAccess && !this.loading
    },
  },

  watch: {
    post_id: {
      handler: function (id) {
        this.getPost(id)
      },

      deep: true,
    },

    edit: {
      handler: function (val) {
        this.$set(this, 'editingPost', val)
      },

      deep: true,
    },
  },

  created() {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
  },

  async mounted() {
    await this.getPost(this.post_id, this.edit)

    EventBus.$on('update_post_success', (id) => {
      this.getPost(id)
    })
  },

  unmounted() {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    ...mapActions({
      dispatchUpdateCurrentPost: 'shared/dispatchUpdateCurrentPost',
      dispatchSetCurrentPost: 'shared/dispatchSetCurrentPost',
      dispatchGetContributorPost: 'contributors/dispatchGetPost',
      dispatchLogPostView: 'careerwise/dispatchLogPostView',
    }),

    updateLoading(val) {
      this.$set(this, 'loading', val)
    },

    onEditPost() {
      this.$set(this, 'editingPost', !this.editingPost)
    },

    handleResize() {
      this.window.width = window.innerWidth
      this.window.height = window.innerHeight
    },

    goBack() {
      if (Path === '/') {
        if (this.isContributor) {
          this.$router.push({ path: '/cloud/posts' })
        } else {
          this.$router.push({ path: '/posts' })
        }
      } else {
        this.$router.go(-1)
      }
    },

    async getPost(id, editingPost = false) {
      this.$set(this, 'loading', true)
      this.dispatchSetCurrentPost()

      const action = this.isContributor
        ? 'dispatchGetContributorPost'
        : 'dispatchUpdateCurrentPost'
      await this[action](id)
      this.$set(this, 'editingPost', editingPost)
      this.trackCurrentRoute(this.currentPost)

      this.$set(this, 'loading', false)
    },
  },
}
</script>

<style scoped>
.post-intro {
  height: 1px;
  width: 500px;
  min-width: 95%;
  position: absolute;
  top: 4rem;
  right: 0;
}
</style>
